import {Injectable} from "@angular/core";
import {TransportService} from "./transport.service";
import {
    AdministratorCommandsNamespace,
    BaseCommandMessage,
    BaseResponseMessage,
    COMMAND_PING,
    ComplaintNamespace,
    RoomAdministration,
    RoomsNamespace,
    SendToIgnoreNamespace
} from "../../../shared/commands";
import {AnalyticsService} from "./analytics.service";
import {removeElementFromArray} from "../../../shared/utils";
import {environment} from "../../environments/environment";
import {LocalModeratorPermissions} from "../../../shared/room";
import {Roles} from "../../../shared/user";
import {RoomBanReasonData} from "../../../shared/bans";
import {Payments} from "../../../shared/commands/payments";
import {ConnectCommandNamespace} from "../../../shared/commands/connect";
import AddTelegramUsernameToBlock = AdministratorCommandsNamespace.AddTelegramUsernameToBlockCommand;
import UnblockUserCommand = AdministratorCommandsNamespace.UnblockUserCommand;
import Complaint = ComplaintNamespace.Complaint;
import UnblockUserForPaidRoomMessage = RoomAdministration.UnblockUserForPaidRoomCommand;
import ModeratorsListCommand = RoomAdministration.ModeratorsListCommand;
import ChangeModeratorPermissionsCommand = RoomAdministration.ChangeModeratorPermissionsCommand;
import COMMAND_CONNECT = ConnectCommandNamespace.COMMAND_CONNECT;

@Injectable({
    providedIn: 'root'
})
export class RemoteCommandService {

    static SERVICE_COMMAND_SOCKET_CONNECT = "service_command_socket_connect"
    static SERVICE_COMMAND_SOCKET_DISCONNECT = "service_command_socket_disconnect"
    static SERVICE_COMMAND_SOCKET_ERROR = "service_command_socket_error"

    private pendingRequests: Map<number, Request<BaseCommandMessage>> = new Map();
    private persistenceCallbacks: Map<string, Array<RemoteCommandCallback<any>>> = new Map()

    constructor(private _socketService: TransportService, private _analyticsService: AnalyticsService,) {
        this.subscribeOnWebsocket()

        this.subscribeOnCommand(COMMAND_CONNECT, (message) => {
            this.onConnectResponseReceived(message)
        })
    }

    get isConnected(): boolean {
        return this._socketService.isConnected()
    }

    async addTelegramUsernameToBlock(username: string): Promise<BaseResponseMessage> {
        return this.sendCommandWithCallback(new AddTelegramUsernameToBlock(username))
    }

    async assignGlobalModerator(login: string): Promise<BaseResponseMessage> {
        return this.sendCommandWithCallback(new AdministratorCommandsNamespace.AssignGlobalModeratorCommand(login))
    }

    async takeOffGlobalModeratorRole(login: string): Promise<BaseResponseMessage> {
        return this.sendCommandWithCallback(new AdministratorCommandsNamespace.TakeOffGlobalModeratorRoleCommand(login))
    }

    async changeUserPermissions(room: string, login: string, permissions: Array<LocalModeratorPermissions | Roles>): Promise<BaseResponseMessage> {
        return this.sendCommandWithCallback(new ChangeModeratorPermissionsCommand(room, login, permissions))
    }

    async unblockUser(login: string): Promise<BaseResponseMessage> {
        return this.sendCommandWithCallback(new UnblockUserCommand(login))
    }

    async unblockUserPaidRoom(login: string, room: string): Promise<BaseResponseMessage> {
        return this.sendCommandWithCallback(new UnblockUserForPaidRoomMessage(login, room))
    }

    async moderators(room: string): Promise<BaseResponseMessage> {
        return this.sendCommandWithCallback(new ModeratorsListCommand(room))
    }

    async roomBanReasons(room: string): Promise<BaseResponseMessage> {
        return this.sendCommandWithCallback(new RoomAdministration.RoomBanReasonsCommand(room))
    }

    async editRoomBanReason(room: string, reason: RoomBanReasonData): Promise<BaseResponseMessage> {
        return this.sendCommandWithCallback(new RoomAdministration.EditRoomBanReasonCommand(room, reason))
    }

    async deleteRoomBanReason(room: string, id: string): Promise<BaseResponseMessage> {
        return this.sendCommandWithCallback(new RoomAdministration.DeleteRoomBanReasonCommand(room, id))
    }

    /**
     *
     * @param amount сума в центах
     */
    async createInvoice(amount: number): Promise<BaseResponseMessage> {
        return this.sendCommandWithCallback(new Payments.CreateInvoiceCommand(amount))
    }

    async transactions(start: number, limit: number): Promise<Payments.TransactionsCommandResponse> {
        return this.sendCommandWithCallback(new Payments.TransactionsCommand(start, limit))
    }

    async closeRoom(room: string): Promise<BaseResponseMessage> {
        return this.sendCommandWithCallback(new RoomsNamespace.CloseRoomCommand(room))
    }

    complaint(login: string, message: string, complaint: Complaint, room: string) {
        this.sendCommand(new ComplaintNamespace.ComplaintMessage(login, message, complaint, room))
    }

    ignoreUser(login: string) {
        this.sendCommand(new SendToIgnoreNamespace.CommandMessage(login, false))
    }

    connect(login: string, sex: string, password: string, invisible: boolean, hash: string, captcha: string) {
        let parameters: Array<{ name: string; value: string }> = [];

        parameters.push(
            {name: 'login', value: login},
            {name: 'sex', value: sex},
            {name: 'hash', value: hash},
            {name: 'captcha', value: captcha},
            {name: 'version', value: environment.version},
        )

        if (password) {
            parameters.push(
                {name: 'password', value: password},
                {name: 'invisible', value: invisible ? 'true' : 'false'},
            )
        }

        if (!environment.production) {
            parameters.push(
                {name: 'allowAnyHost', value: 'true'}
            )
        }

        this._socketService.connect(parameters)
    }

    reconnect(): boolean {
        let session = window.sessionStorage.getItem('session')

        if (!session) return false

        let parameters: Array<{ name: string; value: string }> = [];

        parameters.push(
            {name: 'session', value: session},
            {name: 'version', value: environment.version}
        )

        this._socketService.connect(parameters)

        return true
    }

    disconnect() {
        window.sessionStorage.removeItem('session')
        this._socketService.disconnect()
    }

    subscribeOnCommand<T>(command: string, callback: RemoteCommandCallback<T>) {
        let callbacksArray = this.persistenceCallbacks.get(command)
        if (!callbacksArray) {
            callbacksArray = new Array<RemoteCommandCallback<T>>()
            this.persistenceCallbacks.set(command, callbacksArray)
        }
        callbacksArray.push(callback)
    }

    unsubscribeFromCommand<T>(command: string, callback: RemoteCommandCallback<T>) {
        let callbacksArray = this.persistenceCallbacks.get(command)

        if (!callbacksArray) {
            console.warn(`No callbacks for command ${command}`)
        }

        removeElementFromArray(callbacksArray, callback)
    }

    unsubscribeCallback<T>(callback: RemoteCommandCallback<T>) {
        this.persistenceCallbacks.forEach(callbacksArray => {
            removeElementFromArray(callbacksArray, callback)
        })
    }

    sendCommand(command: BaseCommandMessage) {
        this._socketService.sendMessage(command)
    }

    sendCommandWithCallback<T extends BaseResponseMessage>(command: BaseCommandMessage, requestTimeoutInMs: number = 10000): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            const request: Request<T> = {
                command: command,
                resolve,
                reject,
                timeout: setTimeout(() => {
                    this.pendingRequests.delete(command.id);
                    reject(new Error(`Request timeout: ${command.cmd}:${command.id}`));
                }, requestTimeoutInMs),
            };

            this.sendRequest(request);
        });
    }

    private sendRequest<T extends BaseCommandMessage>(request: Request<T>) {
        if (!this.isConnected) {
            request.reject(new Error(`Connection closed: ${request.command.cmd}`));
            return;
        }

        this.pendingRequests.set(request.command.id, request as Request<BaseCommandMessage>);

        //this.socket?.send(this.codec.encode(request.message));
        this._socketService.sendMessage(request.command)
    }

    private subscribeOnWebsocket() {
        this._socketService.onConnect(() => {
            this.onMessage(new BaseResponseMessage(RemoteCommandService.SERVICE_COMMAND_SOCKET_CONNECT, 0, true))
        })

        this._socketService.onDisconnect((statusCode) => {
            this.onMessage(new DisconnectResponseMessage(statusCode))
            this._analyticsService.serverDisconnected();
        });

        this._socketService.onError(() => {
            this.onMessage(new BaseResponseMessage(RemoteCommandService.SERVICE_COMMAND_SOCKET_ERROR, 0, false))
        })

        this._socketService.onMessage((message: object) => {
            this.onMessage(message as BaseResponseMessage);
        });
    }

    private onMessage(message: BaseResponseMessage) {
        //skip ping command
        if(message.cmd == COMMAND_PING) return

        const request = this.pendingRequests.get(message.id);

        if (request) { // We have a request saved, so this is a response
            clearTimeout(request.timeout);
            this.pendingRequests.delete(message.id);

            request.resolve(message as BaseResponseMessage);
        } else if (this.persistenceCallbacks.has(message.cmd)) {
            this.persistenceCallbacks.get(message.cmd).forEach((callback) => {
                callback(message)
            })
        } else {
            if (message.cmd !== RemoteCommandService.SERVICE_COMMAND_SOCKET_CONNECT && message.cmd !== RemoteCommandService.SERVICE_COMMAND_SOCKET_DISCONNECT && message.cmd !== RemoteCommandService.SERVICE_COMMAND_SOCKET_ERROR) {
                if (!environment.production) {
                    console.log(`Not found callback for response: cmd=${message.cmd}, id=${message.id}`)
                }
            }
        }
    }

    private onConnectResponseReceived(message: BaseResponseMessage) {
        if (message.success) {
            let response = message as ConnectCommandNamespace.Response

            window.sessionStorage.setItem('session', response.session)
        }
    }
}

export interface Request<T> {
    command: BaseCommandMessage;
    resolve: (data: T) => void;
    reject: (error: Error) => void;
    timeout: NodeJS.Timeout;
}

export type RemoteCommandCallback<T> = (T: BaseResponseMessage) => void

export class DisconnectResponseMessage extends BaseResponseMessage {
    constructor(readonly statusCode: number) {
        super(RemoteCommandService.SERVICE_COMMAND_SOCKET_DISCONNECT, 0, true);
    }
}